import { useState, useCallback, useMemo } from 'react';
import ClientRequestService, {
  type ClientRequestStatus,
  type ClientRequest,
  type NewClientRequest,
  type ClientRequestList,
} from './ClientRequestService';

/**
 * Return type for the useClientRequest hook.
 */
interface UseClientRequestReturn {
  /** The current request object */
  request: ClientRequest | null;

  /** Function to manually update the request state */
  setRequest: React.Dispatch<React.SetStateAction<ClientRequest | null>>;

  /** Loading state indicating if an API call is in progress */
  loading: boolean;

  /** Error message from the last failed operation, or null if no error */
  error: string | null;

  /** Function to manually set or clear the error state */
  setError: React.Dispatch<React.SetStateAction<string | null>>;

  /** Updates only the status of the current request */
  updateStatus: (status: ClientRequestStatus) => Promise<void>;

  /** Updates the entire request with new data */
  updateRequest: (data: Partial<ClientRequest>) => Promise<ClientRequest>;

  /** Creates a new request */
  createRequest: (data: NewClientRequest) => Promise<ClientRequest>;

  /** Retrieves the list of all client requests */
  getRequestList: () => Promise<ClientRequestList>;
}

/**
 * Custom React hook for managing client request state and API operations.
 * Provides methods to create, update, and manage the status of client requests.
 * Handles loading states, error handling, and state updates automatically.
 *
 * This hook mirrors the functionality of the Angular ClientRequest model and
 * RequestItemCtrl controller, providing a React-friendly interface for the same operations.
 *
 * @param clientId - The ID of the client for which requests will be managed
 * @param initialRequest - Optional initial request object (default: null)
 * @returns {UseClientRequestReturn} Object containing request state, loading state, error state, and methods
 *
 * @example
 * ```tsx
 * const { request, loading, error, updateStatus, createRequest } = useClientRequest('client123', existingRequest);
 *
 * // Create a new request
 * await createRequest({ name: 'New Request', requestType: 'GENERAL' });
 *
 * // Update request status
 * await updateStatus('COMPLETED');
 * ```
 */
export const useClientRequest = (
  clientId: string,
  initialRequest: ClientRequest | null = null,
): UseClientRequestReturn => {
  const [request, setRequest] = useState(initialRequest);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize the service instance to avoid recreating it on every render
  const service = useMemo(() => new ClientRequestService(clientId), [clientId]);

  /**
   * Updates only the status of the current request.
   *
   * @param status - The new status value (e.g., 'COMPLETED', 'CREATED')
   * @returns Promise that resolves with the updated request data
   * @throws {Error} If the status update fails
   */
  const updateStatus = useCallback(
    async (status: ClientRequestStatus) => {
      setLoading(true);
      setError(null);
      try {
        await service.updateStatus((request as ClientRequest).id, status);
        setRequest((prev: any) => ({ ...prev, status }));
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.messages?.[0]?.message || 'Problem found! Please try again!';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service, request],
  );

  /**
   * Updates an existing request with new data.
   * Merges the provided data with the existing request state.
   *
   * @param data - The data to update in the request
   * @returns Promise that resolves with the updated request data
   * @throws {Error} Error If the update fails
   */
  const updateRequest = useCallback(
    async (data: Partial<ClientRequest>) => {
      setLoading(true);
      setError(null);

      try {
        const updatedRequest = await service.update((request as ClientRequest).id, data);
        setRequest((prev: any) => ({ ...prev, ...data }));
        return updatedRequest as ClientRequest;
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.messages?.[0]?.message || 'Problem found! Please try again!';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service, request],
  );

  /**
   * Creates a new client request.
   * Replaces the current request state with the newly created request.
   *
   * @param data - The data for the new request
   * @returns Promise that resolves with the newly created request data
   * @throws {Error} If the creation fails
   */
  const createRequest = useCallback(
    async (data: NewClientRequest) => {
      setLoading(true);
      setError(null);

      try {
        const newRequest = await service.create(data);
        setRequest(newRequest);
        return newRequest;
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.messages?.[0]?.message || 'Problem found! Please try again!';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [service],
  );

  /**
   * Retrieves the list of all client requests.
   * Returns a ClientRequestList containing total count, completed count, and all requests.
   *
   * @returns Promise that resolves with the client request list data
   * @throws {Error} If the list retrieval fails
   */
  const getRequestList = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const requestList = await service.list();
      return requestList;
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.messages?.[0]?.message || 'Problem found! Please try again!';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service]);

  return {
    request,
    setRequest,
    loading,
    error,
    setError,
    updateStatus,
    updateRequest,
    createRequest,
    getRequestList,
  };
};
